import { UIDataType, UISnowAttribute } from 'src/@types/serviceOnboardingTypes/attribute.types';
import {
	AuthType,
	HttpMethod,
	OperationAuthType,
	OperationMetadata,
	RequestObject,
	ApiGateWayRequestParameters,
	Status,
	ResponseObject,
	CreateOperationRequest,
	UpdateOperationRequest,
	ExecutionType,
} from '@amzn/snow-data-service-typescript-client';
import { WizardStep } from 'src/components/commonComponents/GenericWizard/@types/GenericForm.types';
import {
	createWizardStep,
	createConditionalWizardStep,
} from 'src/components/commonComponents/GenericWizard/utils/wizardUtils';
import { parseCommaSeparatedString } from 'src/utils/StringUtils';
import { Override } from 'src/@types/common';
import { IntegrationType, Protocol, Encoding } from 'src/@types/serviceOnboardingTypes/operation.interface';

export type OperationProfileFormMetadata = Override<
	OperationMetadata,
	{
		requestObject: RequestConfigFormMetaData;
		operationAccessBindles: string;
		configurations: string;
		useServiceNamespaceAsOperationNamespace: string;
	}
>;
type RequestConfigFormMetaData = Override<
	RequestObject,
	{
		stageToDefaultRegionMapping: string;
		stageToEndpointsMapping: string;
		apiGateWayRequestParameters: ApiGateWayRequestParametersFormMetaData;
	}
>;
type ApiGateWayRequestParametersFormMetaData = Override<
	ApiGateWayRequestParameters,
	{ headers: string; queryParameters: string }
>;

export const OPERATION_BASIC_INFO_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'operationName',
		attributeName: 'Operation Name',
		description:
			'Enter the activity name, function name, or API name that is registered in AAA application, AWS function, or API endpoint',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Provide the exact name as registered in your integration system (e.g., "CreateUser", "ProcessPayment", "GetOrderStatus")',
	},
	{
		attributeId: 'version',
		attributeName: 'Version',
		description:
			'Specify the version number for this operation, used for operation versioning and lifecycle management',
		dataType: UIDataType.NUMBER,
		mandatory: true,
		helpText:
			'Enter a positive integer representing the operation version. This will be used for future versioning and compatibility management.',
	},
	{
		attributeId: 'description',
		attributeName: 'Description',
		description:
			'Provide a comprehensive description explaining how customers can use this operation and its impact on the system',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		helpText:
			"Describe the operation's purpose, expected usage patterns, business impact, and any important considerations for customers using this operation.",
	},
	{
		attributeId: 'idempotent',
		attributeName: 'Idempotent',
		description:
			'Define whether this operation is idempotent, which determines retry behavior in case of operation failures',
		dataType: UIDataType.BOOLEAN,
		mandatory: true,
		helpText:
			'Select true if multiple identical requests have the same effect as a single request. This is crucial for failure handling and retry logic.',
	},
	{
		attributeId: 'status',
		attributeName: 'Status',
		description:
			'Set the operational status that defines whether this operation is currently usable and available',
		dataType: UIDataType.STRING,
		mandatory: true,
		value: Status.ACTIVE,
		options: [Status.ACTIVE, Status.PENDING],
		helpText:
			'Active: Operation is live and usable. Deprecated: Operation is being phased out. Pending: Operation is under development or review.',
	},
	{
		attributeId: 'integrationType',
		attributeName: 'Integration Type',
		description:
			'Select the integration type that defines how the downstream operation is hosted and accessed',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [IntegrationType.APIGATEWAY, IntegrationType.CORAL],
		helpText:
			'Choose the hosting platform: API Gateway for REST APIs, Coral for internal Amazon services',
	},
	{
		attributeId: 'executionType',
		attributeName: 'Execution Type',
		description:
			'Select the execution type that determines how the operation is processed',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [ExecutionType.SYNC, ExecutionType.ASYNC],
		helpText:
			'SYNC: Operation executes synchronously and waits for completion. ASYNC: Operation executes asynchronous flow.',
	},
	{
		attributeId: 'configurations',
		attributeName: 'Configurations',
		description:
			'Specify configuration owners and types that are typically fetched or updated by this operation across AFT',
		dataType: UIDataType.LIST,
		mandatory: false,
		helpText:
			'List the configuration types this operation interacts with. These values should be predefined by SNOW and help identify configuration ownership.',
	},
	{
		attributeId: 'operationAccessBindles',
		attributeName: 'Operation Access Bindles',
		description:
			'Define Bindle-based permissions required to access and execute this operation',
		dataType: UIDataType.LIST,
		mandatory: false,
		regex: '^amzn1\\.bindle\\.resource\\.[a-z0-9]{16,}$',
		helpText:
			'Enter Bindle resource IDs that grant access to this operation. Format: amzn1.bindle.resource.xxxxxxxxxxxxx',
	},
];

export const OPERATION_REQUEST_CONFIG_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'requestObject.timeout',
		attributeName: 'Timeout',
		description:
			'Set the maximum time allowed for this operation to complete before timing out',
		dataType: UIDataType.NUMBER,
		mandatory: false,
		helpText:
			'Enter timeout value in milliseconds. Consider the operation complexity and downstream dependencies when setting this value.',
	},
	{
		attributeId: 'requestObject.requestBody',
		attributeName: 'Request Body',
		description:
			'Define the request body template with complete metadata including custom headers, URL parameters, and payload structure',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		regex: '^\\s*\\{[\\s\\S]*\\}\\s*$',
		helpText:
			'Provide a JSON template that defines the structure and format of the request body. This will vary based on your integration point.',
	},
	{
		attributeId: 'requestObject.stageToDefaultRegionMapping',
		attributeName: 'Stage to Default Region Mapping',
		description:
			'Configure the mapping of deployment stages to their corresponding default AWS regions',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		regex:
			'^\\s*\\{\\s*(?:"(BETA|PROD|GAMMA|ALPHA)"\\s*:\\s*"(IAD|NRT|PDX|DUB|ZAZ)"\\s*,?\\s*)+\\}\\s*$',
		helpText:
			'Map each stage (ALPHA, BETA, GAMMA, PROD) to its default region (IAD, NRT, PDX, DUB, ZAZ). Example: {"PROD": "IAD", "BETA": "PDX"}',
	},
];

export const OPERATION_CORAL_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'requestObject.stageToEndpointsMapping',
		attributeName: 'Stage to Endpoints Mapping',
		description:
			'Configure the mapping of deployment stages to their region-specific endpoints for Coral operation routing. Endpoints MUST include https:// prefix.',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		regex:
			'^\\s*\\{\\s*(?:"(BETA|PROD|GAMMA|ALPHA)"\\s*:\\s*\\{\\s*(?:"(IAD|NRT|PDX|DUB|ZAZ)"\\s*:\\s*"(https:\\/\\/[^\\s"]+)"\\s*,?\\s*)+\\}\\s*,?\\s*)+\\}\\s*$',
		helpText:
			'Map each stage to region-specific endpoints with https:// prefix. Example: {"PROD": {"IAD": "https://api.prod.iad.amazon.com", "PDX": "https://api.prod.pdx.amazon.com"}}',
	},
	{
		attributeId: 'serviceNamespace',
		attributeName: 'Service Namespace',
		description:
			'Enter the Coral service namespace that uniquely identifies your service within the Amazon ecosystem',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Provide the complete namespace following Amazon conventions (format: com.amazon.service.name)',
		regex: '^com\\.amazon\\.[a-z][a-z0-9]*(?:\\.[a-z][a-z0-9]*)*$',
	},
	{
		attributeId: 'useServiceNamespaceAsOperationNamespace',
		attributeName: 'Use Service Namespace as Operation Namespace',
		description:
			'Select Yes if the operation namespace is the same as the service namespace',
		dataType: UIDataType.BOOLEAN,
		mandatory: false,
		helpText:
			'When set to Yes, the service namespace value will be used as the operation namespace.',
	},
	{
		attributeId: 'operationNamespace',
		attributeName: 'Operation Namespace',
		description:
			'Enter the operation namespace if it differs from the service namespace',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Provide the operation namespace if the operation belongs to a different namespace than the service (format: com.amazon.service.name)',
		regex: '^com\\.amazon\\.[a-z][a-z0-9]*(?:\\.[a-z][a-z0-9]*)*$',
	},
	{
		attributeId: 'aaaServiceName',
		attributeName: 'AAA Service Name',
		description:
			'Specify the name of the service onboarded to AAA (Amazon Authentication and Authorization)',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Enter the AAA service name. SNOW will validate this as mandatory based on Integration Type requirements.',
	},
	{
		attributeId: 'superStarServiceName',
		attributeName: 'SuperStar Service Name',
		description:
			'Specify the name of the service onboarded to SuperStar monitoring and observability platform',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Enter the SuperStar service name. SNOW will validate this as mandatory based on Integration Type requirements.',
	},
	{
		attributeId: 'requestObject.operationAuthType',
		attributeName: 'Operation Auth Type',
		description:
			'Select the authentication mechanism used for securing access to this Coral operation',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [OperationAuthType.AwsAuth, OperationAuthType.CloudAuth],
		helpText:
			'Choose AWS Auth for AWS IAM-based authentication or Cloud Auth for Amazon internal authentication systems.',
	},
	{
		attributeId: 'requestObject.protocol',
		attributeName: 'Protocol',
		description:
			'Select the Coral communication protocol version used for this operation',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Choose the appropriate Coral RPC protocol version. Use the latest version unless compatibility requires an older version.',
		options: [Protocol.CORAL_RPC_V0, Protocol.CORAL_RPC_V1, Protocol.RPC_V0],
	},
	{
		attributeId: 'requestObject.encoding',
		attributeName: 'Encoding',
		description:
			'Select the encoding format used for request and response data serialization',
		dataType: UIDataType.STRING,
		mandatory: false,
		helpText:
			'Choose the encoding type that matches your service implementation. BASE_64 is commonly used for binary data.',
		options: [Encoding.BASE_32, Encoding.BASE_64, Encoding.UTF_8],
	},
];

export const OPERATION_API_GATEWAY_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'requestObject.stageToEndpointsMapping',
		attributeName: 'Stage to Endpoints Mapping',
		description:
			'Configure the mapping of deployment stages to their region-specific endpoints for API Gateway routing. Endpoints must NOT include https:// or http:// prefix.',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		regex:
			'^\\s*\\{\\s*(?:"(BETA|PROD|GAMMA|ALPHA)"\\s*:\\s*\\{\\s*(?:"(IAD|NRT|PDX|DUB|ZAZ)"\\s*:\\s*"(?!https?:\\/\\/)([^\\s"]+)"\\s*,?\\s*)+\\}\\s*,?\\s*)+\\}\\s*$',
		helpText:
			'Map each stage to region-specific endpoints WITHOUT protocol prefix. Example: {"PROD": {"IAD": "api.prod.iad.amazon.com", "PDX": "api.prod.pdx.amazon.com"}}',
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.httpMethod',
		attributeName: 'HTTP Method',
		description: 'Select the HTTP method',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [
			HttpMethod.DELETE,
			HttpMethod.GET,
			HttpMethod.PATCH,
			HttpMethod.POST,
			HttpMethod.PUT,
		],
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.stage',
		attributeName: 'Stage',
		description: 'API Gateway stage',
		dataType: UIDataType.STRING,
		mandatory: true,
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.path',
		attributeName: 'Path',
		description: 'API Gateway path',
		dataType: UIDataType.STRING,
		mandatory: true,
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.headers',
		attributeName: 'Headers',
		description: 'Headers',
		dataType: UIDataType.PARAGRAPH,
		mandatory: false,
		regex: '^\\s*\\{[\\s\\S]*\\}\\s*$',
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.queryParameters',
		attributeName: 'Query Parameters',
		description: 'Query Parameters',
		dataType: UIDataType.PARAGRAPH,
		mandatory: false,
		regex: '^\\s*\\{[\\s\\S]*\\}\\s*$',
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.authType',
		attributeName: 'Auth Type',
		description: 'Auth Type',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [AuthType.IAM_ROLE],
	},
	{
		attributeId: 'requestObject.apiGateWayRequestParameters.authCredential',
		attributeName: 'Auth Credential',
		description: 'Auth Credential',
		dataType: UIDataType.STRING,
		mandatory: true,
	},
];

export const OPERATION_AWS_AUTH_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'requestObject.awsAuthParameters.signingRegion',
		attributeName: 'Signing Region',
		description: 'AWS signing region',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: ['us-east-1', 'us-west-2', 'eu-west-1'],
	},
	{
		attributeId: 'requestObject.awsAuthParameters.iamRoleArn',
		attributeName: 'IAM Role ARN',
		description: 'IAM role ARN for authentication',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Enter IAM role ARN (format: arn:aws:iam::account:role/role-name)',
		regex: '^arn:aws:iam::[0-9]{12}:role/[a-zA-Z0-9+=,.@_-]+$',
	},
	{
		attributeId: 'requestObject.awsAuthParameters.method',
		attributeName: 'Method',
		description: 'HTTP method for signing',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [
			HttpMethod.DELETE,
			HttpMethod.GET,
			HttpMethod.PATCH,
			HttpMethod.POST,
			HttpMethod.PUT,
		],
	},
	{
		attributeId: 'requestObject.awsAuthParameters.service',
		attributeName: 'Service',
		description:
			'Specify the AWS service identifier that this configuration applies to. This value should correspond to a valid AWS service or service action, such as execute-api for API Gateway, lambda-invoke for invoking Lambda functions, or other supported service strings.',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Enter the AWS service name or action you want to configure or interact with.',
		regex: '^(execute-api|lambda-invoke)$',
	},
];

export const OPERATION_RESPONSE_CONFIG_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'responseObject.responseBody',
		attributeName: 'Response Body',
		description:
			'Define the response body template with complete metadata about response structure, format, and expected content',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		regex: '^\\s*\\{[\\s\\S]*\\}\\s*$',
		helpText:
			'Provide a JSON template that defines the structure and format of the response body. Include all expected fields and their data types.',
	},
	{
		attributeId: 'responseObject.successCriteria',
		attributeName: 'Success Criteria',
		description:
			'Define the criteria that determine when an operation response should be considered successful',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		helpText:
			'Specify success conditions as a JSON array of objects. Include HTTP status codes, response field values, or other indicators of successful execution.',
		regex: '^\\s*\\[\\s*\\{[\\s\\S]*\\}\\s*\\]\\s*$',
	},
	{
		attributeId: 'responseObject.failureCriteria',
		attributeName: 'Failure Criteria',
		description:
			'Define the criteria that determine when an operation response should be considered failed or requires retry',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		helpText:
			'Specify failure conditions as a JSON array of objects. Include error codes, timeout conditions, or other indicators that require operation retry or failure handling.',
		regex: '^\\s*\\[\\s*\\{[\\s\\S]*\\}\\s*\\]\\s*$',
	},
];

export const DEFAULT_OPERATION_DATA: OperationProfileFormMetadata = {
	applicationId: '',
	operationId: '',
	operationName: '',
	version: 1,
	description: '',
	idempotent: false,
	status: Status.ACTIVE,
	integrationType: IntegrationType.APIGATEWAY,
	serviceNamespace: '',
	operationNamespace: '',
	useServiceNamespaceAsOperationNamespace: 'false',
	aaaServiceName: '',
	superStarServiceName: '',
	executionType: ExecutionType.SYNC,
	requestObject: {
		operationAuthType: OperationAuthType.CloudAuth,
		requestBody: '{}',
		stageToDefaultRegionMapping: '{}',
		stageToEndpointsMapping: '{}',
		timeout: 100000,
		protocol: Protocol.CORAL_RPC_V0,
		encoding: Encoding.BASE_64,
		apiGateWayRequestParameters: {
			httpMethod: HttpMethod.GET,
			stage: '',
			path: '/',
			authType: AuthType.IAM_ROLE,
			authCredential: '',
			headers: '{}',
			queryParameters: '{}',
		},
		awsAuthParameters: {
			signingRegion: '',
			iamRoleArn: '',
			method: HttpMethod.GET,
			service: '',
		},
	},
	responseObject: {
		responseBody: '',
		successCriteria: '',
		failureCriteria: '',
	},
	createdBy: '',
	lastUpdatedBy: '',
	requestId: '',
	operationAccessBindles: '',
	configurations: '',
};

export function getOperationWizardSteps(formData?: Partial<OperationProfileFormMetadata>): WizardStep<OperationProfileFormMetadata>[] {
	const useSameNamespace = formData?.useServiceNamespaceAsOperationNamespace === 'true';
	const coralAttributes = useSameNamespace
		? OPERATION_CORAL_ATTRIBUTES.map((attr) =>
			attr.attributeId === 'operationNamespace'
				? { ...attr, disabled: true, value: formData?.serviceNamespace || '' }
				: attr
		)
		: OPERATION_CORAL_ATTRIBUTES;

	return [
		createWizardStep<OperationProfileFormMetadata>(
			'Basic Information',
			OPERATION_BASIC_INFO_ATTRIBUTES,
			{
				description:
					'Configure essential operation details including name, version, description, and operational characteristics. This information defines how the operation will be identified and managed within SNOW.',
				reviewGroup: 'Basic Information',
			}
		),
		createWizardStep<OperationProfileFormMetadata>(
			'Basic Request Configuration',
			OPERATION_REQUEST_CONFIG_ATTRIBUTES,
			{
				description:
					'Set up core request parameters including timeout settings, request body templates, and stage-to-region mappings. This configuration determines how requests are routed and processed.',
				reviewGroup: 'Request Configuration',
			}
		),
		createConditionalWizardStep<OperationProfileFormMetadata>(
			'Coral API Configuration',
			coralAttributes,
			'Coral configuration is only applicable when Integration Type is set to Coral.',
			{
				condition: (formData) =>
					formData.integrationType === IntegrationType.CORAL,
				description:
					'Configure Coral-specific settings including service namespace, AAA/SuperStar service names, authentication type, protocol, and encoding. These settings are essential for Coral service integration.',
				reviewGroup: 'Coral Configuration',
			}
		),
		createConditionalWizardStep<OperationProfileFormMetadata>(
			'API Gateway Configuration',
			OPERATION_API_GATEWAY_ATTRIBUTES,
			'API Gateway configuration is only applicable when Integration Type is set to API Gateway.',
			{
				condition: (formData) =>
					formData.integrationType === IntegrationType.APIGATEWAY,
				description:
					'Configure API Gateway-specific parameters including HTTP methods, stages, paths, headers, query parameters, and authentication credentials for REST API integration.',
				reviewGroup: 'API Gateway Configuration',
			}
		),
		createConditionalWizardStep<OperationProfileFormMetadata>(
			'AWS Auth Configuration',
			OPERATION_AWS_AUTH_ATTRIBUTES,
			'AWS Auth configuration is only applicable when Authentication Type is set to AWS Auth.',
			{
				condition: (formData) =>
					formData.requestObject?.operationAuthType ===
					OperationAuthType.AwsAuth,
				description:
					'Configure AWS authentication parameters including signing region, IAM role ARN, and HTTP method for AWS signature-based authentication.',
				reviewGroup: 'AWS Auth Configuration',
			}
		),
		createWizardStep<OperationProfileFormMetadata>(
			'Response Configuration',
			OPERATION_RESPONSE_CONFIG_ATTRIBUTES,
			{
				description:
					'Define response handling including response body templates, success criteria, and failure criteria. This configuration determines how operation responses are processed and evaluated.',
				reviewGroup: 'Response Configuration',
			}
		),
	];
}

export function convertToCreateOperationRequest(
	formData: OperationProfileFormMetadata,
	applicationId: string,
	requestId: string
): CreateOperationRequest {
	const requestObject: RequestObject = {
		timeout: formData.requestObject?.timeout,
		protocol: formData.requestObject?.protocol,
		encoding: formData.requestObject?.encoding,
		operationAuthType: formData.requestObject?.operationAuthType,
		requestBody: formData.requestObject?.requestBody,
		stageToDefaultRegionMapping: JSON.parse(
			formData.requestObject?.stageToDefaultRegionMapping
		),
		stageToEndpointsMapping: JSON.parse(
			formData.requestObject?.stageToEndpointsMapping
		),
	};

	if (formData.integrationType === IntegrationType.APIGATEWAY) {
		requestObject.apiGateWayRequestParameters = {
			httpMethod:
				formData.requestObject?.apiGateWayRequestParameters?.httpMethod,
			stage: formData.requestObject?.apiGateWayRequestParameters?.stage,
			path: formData.requestObject?.apiGateWayRequestParameters?.path,
			headers: JSON.parse(
				formData.requestObject?.apiGateWayRequestParameters?.headers
			),
			queryParameters: JSON.parse(
				formData.requestObject?.apiGateWayRequestParameters?.queryParameters
			),
			authType: formData.requestObject?.apiGateWayRequestParameters?.authType,
			authCredential:
				formData.requestObject?.apiGateWayRequestParameters?.authCredential,
		};
	}

	if (formData.requestObject?.operationAuthType === OperationAuthType.AwsAuth) {
		requestObject.awsAuthParameters = {
			signingRegion: formData.requestObject?.awsAuthParameters?.signingRegion,
			iamRoleArn: formData.requestObject?.awsAuthParameters?.iamRoleArn,
			method: formData.requestObject?.awsAuthParameters?.method,
			service: formData.requestObject?.awsAuthParameters?.service,
		};
	}

	const responseObject: ResponseObject = {
		responseBody: formData.responseObject?.responseBody,
		successCriteria: formData.responseObject?.successCriteria,
		failureCriteria: formData.responseObject?.failureCriteria,
	};

	return {
		applicationId,
		operationName: formData.operationName,
		version: formData.version,
		description: formData.description,
		idempotent: formData.idempotent,
		status: formData.status,
		integrationType: formData.integrationType,
		executionType: formData.executionType,
		requestObject,
		responseObject,
		serviceNamespace: formData.serviceNamespace,
		aaaServiceName: formData.aaaServiceName,
		superStarServiceName: formData.superStarServiceName,
		createdBy: formData.createdBy,
		lastUpdatedBy: formData.lastUpdatedBy,
		configurations: parseCommaSeparatedString(formData.configurations),
		operationAccessBindles: parseCommaSeparatedString(
			formData.operationAccessBindles
		),
		requestId,
		operationNamespace: formData.useServiceNamespaceAsOperationNamespace === 'true'
			? formData.serviceNamespace
			: formData.operationNamespace,
	};
}

export function convertFromOperationMetadata(
	metadata: OperationMetadata
): OperationProfileFormMetadata {
	return {
		applicationId: metadata.applicationId,
		operationId: metadata.operationId,
		operationName: metadata.operationName,
		version: metadata.version,
		description: metadata.description,
		idempotent: metadata.idempotent,
		status: metadata.status,
		integrationType: metadata.integrationType,
		serviceNamespace: metadata.serviceNamespace || '',
		operationNamespace: metadata.operationNamespace || '',
		useServiceNamespaceAsOperationNamespace:
			(metadata.serviceNamespace && metadata.operationNamespace &&
				metadata.serviceNamespace === metadata.operationNamespace) ? 'true' : 'false',
		aaaServiceName: metadata.aaaServiceName || '',
		superStarServiceName: metadata.superStarServiceName || '',
		executionType: metadata.executionType || ExecutionType.SYNC,
		createdBy: metadata.createdBy || '',
		lastUpdatedBy: metadata.lastUpdatedBy || '',
		configurations: metadata.configurations?.join(', ') || '',
		operationAccessBindles: metadata.operationAccessBindles?.join(', ') || '',
		requestObject: {
			timeout: metadata.requestObject?.timeout,
			protocol: metadata.requestObject?.protocol,
			encoding: metadata.requestObject?.encoding,
			operationAuthType: metadata.requestObject?.operationAuthType,
			requestBody: metadata.requestObject?.requestBody || '',
			stageToDefaultRegionMapping: JSON.stringify(
				metadata.requestObject?.stageToDefaultRegionMapping || {},
				null,
				2
			),
			stageToEndpointsMapping: JSON.stringify(
				metadata.requestObject?.stageToEndpointsMapping || {},
				null,
				2
			),
			apiGateWayRequestParameters: {
				httpMethod: metadata.requestObject?.apiGateWayRequestParameters?.httpMethod,
				stage: metadata.requestObject?.apiGateWayRequestParameters?.stage,
				path: metadata.requestObject?.apiGateWayRequestParameters?.path || '',
				headers: JSON.stringify(
					metadata.requestObject?.apiGateWayRequestParameters?.headers || {},
					null,
					2
				),
				queryParameters: JSON.stringify(
					metadata.requestObject?.apiGateWayRequestParameters?.queryParameters || {},
					null,
					2
				),
				authType: metadata.requestObject?.apiGateWayRequestParameters?.authType,
				authCredential: metadata.requestObject?.apiGateWayRequestParameters?.authCredential || '',
			},
			awsAuthParameters: metadata.requestObject?.awsAuthParameters,
		},
		responseObject: metadata.responseObject,
		requestId: metadata.requestId,
	};
}

export function convertFromOperationProfileData(
	operation: OperationMetadata
): OperationProfileFormMetadata {
	return convertFromOperationMetadata(operation);
}

export function convertToUpdateOperationRequest(
	formData: OperationProfileFormMetadata,
	operationId: string,
	applicationId: string,
	userName: string
): UpdateOperationRequest {
	const requestObject: RequestObject = {
		timeout: formData.requestObject?.timeout,
		protocol: formData.requestObject?.protocol,
		encoding: formData.requestObject?.encoding,
		operationAuthType: formData.requestObject?.operationAuthType,
		requestBody: formData.requestObject?.requestBody,
		stageToDefaultRegionMapping: JSON.parse(
			formData.requestObject?.stageToDefaultRegionMapping
		),
		stageToEndpointsMapping: JSON.parse(
			formData.requestObject?.stageToEndpointsMapping
		),
	};

	if (formData.integrationType === IntegrationType.APIGATEWAY) {
		requestObject.apiGateWayRequestParameters = {
			httpMethod:
				formData.requestObject?.apiGateWayRequestParameters?.httpMethod,
			stage: formData.requestObject?.apiGateWayRequestParameters?.stage,
			path: formData.requestObject?.apiGateWayRequestParameters?.path,
			headers: JSON.parse(
				formData.requestObject?.apiGateWayRequestParameters?.headers
			),
			queryParameters: JSON.parse(
				formData.requestObject?.apiGateWayRequestParameters?.queryParameters
			),
			authType: formData.requestObject?.apiGateWayRequestParameters?.authType,
			authCredential:
				formData.requestObject?.apiGateWayRequestParameters?.authCredential,
		};
	}

	if (formData.requestObject?.operationAuthType === OperationAuthType.AwsAuth) {
		requestObject.awsAuthParameters = {
			signingRegion: formData.requestObject?.awsAuthParameters?.signingRegion,
			iamRoleArn: formData.requestObject?.awsAuthParameters?.iamRoleArn,
			method: formData.requestObject?.awsAuthParameters?.method,
			service: formData.requestObject?.awsAuthParameters?.service,
		};
	}

	const responseObject: ResponseObject = {
		responseBody: formData.responseObject?.responseBody,
		successCriteria: formData.responseObject?.successCriteria,
		failureCriteria: formData.responseObject?.failureCriteria,
	};

	return {
		operationId,
		operationName: formData.operationName,
		version: formData.version,
		description: formData.description,
		idempotent: formData.idempotent,
		status: formData.status,
		integrationType: formData.integrationType,
		executionType: formData.executionType,
		requestObject,
		responseObject,
		serviceNamespace: formData.serviceNamespace,
		aaaServiceName: formData.aaaServiceName,
		superStarServiceName: formData.superStarServiceName,
		lastUpdatedBy: userName,
		configurations: parseCommaSeparatedString(formData.configurations),
		operationAccessBindles: parseCommaSeparatedString(
			formData.operationAccessBindles
		),
		requestId: formData.requestId || '',
		operationNamespace: formData.useServiceNamespaceAsOperationNamespace === 'true'
			? formData.serviceNamespace
			: formData.operationNamespace,
	};
}
